// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type {
  TowerData, Meta, SiteTable, Region, History, SwitchOff, Additionality, Capture,
  ScatterPoint, Insight, Carrier, Band,
} from './types';

async function json<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { signal });
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return (await res.json()) as T;
}

export async function loadData(signal?: AbortSignal): Promise<TowerData> {
  const [meta, sites, regions, history, switchoff, additionality, capture, ignored, insights] =
    await Promise.all([
      json<Meta>('data/meta.json', signal),
      json<SiteTable>('data/sites.json', signal),
      json<Region[]>('data/regions.json', signal),
      json<History>('data/history.json', signal),
      json<SwitchOff>('data/switchoff.json', signal),
      json<Additionality>('data/additionality.json', signal),
      json<Capture[]>('data/capture.json', signal),
      json<{ medianBasePerArea: number; medianFundedPerArea: number; points: ScatterPoint[] }>('data/ignored.json', signal),
      json<Insight[]>('data/insights.json', signal),
    ]);

  const bandIndex = new Map(meta.bands.map((b, i) => [b.id, i]));
  const maskOf = (ids: string[]) => ids.reduce((m, id) => m | (1 << (bandIndex.get(id) ?? 0)), 0);
  const genMask = (gen: string) =>
    meta.bands.reduce((m, b, i) => (b.gen === gen ? m | (1 << i) : m), 0);

  return {
    meta,
    sites,
    regions,
    history,
    switchoff,
    additionality,
    capture,
    ignored,
    insights,
    regionByCode: new Map(regions.map((r, i) => [r.code, i])),
    bandIndex,
    masks: {
      modernLow: maskOf(meta.bandGroups.modernLow),
      legacyLow: maskOf(meta.bandGroups.legacyLow),
      mid: maskOf(meta.bandGroups.mid),
      mmwave: maskOf(meta.bandGroups.mmwave),
      g5: genMask('5G'),
      g3: genMask('3G'),
    },
  };
}

/** Band ids carried by a site, in ascending frequency order. */
export function bandsOf(data: TowerData, mask: number): Band[] {
  const out: Band[] = [];
  data.meta.bands.forEach((b, i) => {
    if (mask & (1 << i)) out.push(b);
  });
  return out.sort((a, b) => a.mhz - b.mhz || a.gen.localeCompare(b.gen));
}

export function carrierOf(data: TowerData, i: number): Carrier {
  return data.meta.carriers[i];
}

/**
 * Uniform lat/lon grid over the site table, built once at boot. 25,930 points
 * is small, but the My Address view runs a nearest-neighbour query on every
 * pointer drag, so a linear scan there would be visibly laggy.
 */
export interface SiteGrid {
  cell: number;
  buckets: Map<string, number[]>;
}

export function buildSiteGrid(sites: SiteTable, cell = 0.25): SiteGrid {
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < sites.n; i++) {
    const key = `${Math.floor(sites.lat[i] / cell)},${Math.floor(sites.lon[i] / cell)}`;
    const b = buckets.get(key);
    if (b) b.push(i);
    else buckets.set(key, [i]);
  }
  return { cell, buckets };
}

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Nearest site index matching `accept`, searching outward ring by ring.
 * Stops only once the current ring's guaranteed inner radius exceeds the best
 * distance found — stopping at the first hit returns a near-neighbour, not the
 * nearest one.
 */
export function nearestSite(
  sites: SiteTable,
  grid: SiteGrid,
  lat: number,
  lon: number,
  accept: (i: number) => boolean,
  maxRings = 60,
): { index: number; km: number } | null {
  const cy = Math.floor(lat / grid.cell);
  const cx = Math.floor(lon / grid.cell);
  let best = -1;
  let bestKm = Infinity;
  // A 0.25° cell is at most ~28 km across; one ring guarantees that radius.
  const ringKm = grid.cell * 85;
  for (let ring = 0; ring <= maxRings; ring++) {
    for (let dy = -ring; dy <= ring; dy++) {
      for (let dx = -ring; dx <= ring; dx++) {
        if (Math.max(Math.abs(dy), Math.abs(dx)) !== ring) continue;
        const bucket = grid.buckets.get(`${cy + dy},${cx + dx}`);
        if (!bucket) continue;
        for (const i of bucket) {
          if (!accept(i)) continue;
          const d = haversineKm(lat, lon, sites.lat[i], sites.lon[i]);
          if (d < bestKm) {
            bestKm = d;
            best = i;
          }
        }
      }
    }
    if (best >= 0 && bestKm < ring * ringKm) break;
  }
  return best >= 0 ? { index: best, km: bestKm } : null;
}
